import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import Spinner from '../components/Spinner';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import CommentsSection from '../components/CommentsSection';
import ShareButton from '../components/ShareButton';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { AuthContext } from '../context/AuthContext';
import toast from 'react-hot-toast';
import AuthorBox from '../components/AuthorBox';
import ArticleCard from '../components/ArticleCard';

const ArticleDetailPage = () => {
    const { id } = useParams();
    const { user, token } = useContext(AuthContext);
    
    // SSR'dan gelen initial data'yı kullan
    const initialData = typeof window !== 'undefined' ? window.__INITIAL_DATA__ : null;
    const [article, setArticle] = useState(initialData?.article || null);
    const [loading, setLoading] = useState(!initialData?.article);
    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(initialData?.article?.likeCount || 0);
    const [relatedArticles, setRelatedArticles] = useState([]);

    useEffect(() => {
        // SSR'dan gelen initial data'yı bir kez kullandıktan sonra temizle
        // (client-side navigation'da bir sonraki sayfa yanlış data görmesin)
        if (typeof window !== 'undefined') window.__INITIAL_DATA__ = null;

        // Eğer SSR'dan data geldiyse, tekrar fetch etme
        if (initialData?.article) {
            // Slug/ID doğrulaması: bu data gerçekten mevcut URL'ye mi ait?
            const slugMatch = initialData.article.slug === id;
            const idMatch = initialData.article._id?.toString() === id;
            if (!slugMatch && !idMatch) {
                // Farklı bir makaleye ait data — fetch yap
                setArticle(null);
                setLoading(true);
            } else {
                // Sadece like durumunu kontrol et
                if (user?.id && initialData.article.likes) {
                    const userLiked = initialData.article.likes.some(likeId => {
                        const likeIdStr = typeof likeId === 'object' ? likeId.toString() : String(likeId);
                        const userIdStr = String(user.id);
                        return likeIdStr === userIdStr;
                    });
                    setIsLiked(userLiked);
                }
                return;
            }
        }
        
        // Eğer SSR'dan notFound gelirse, API isteği atma
        if (initialData?.notFound) {
            setLoading(false);
            setArticle(null);
            return;
        }
        
        // SSR data yoksa, normal fetch
        const fetchArticle = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_BASE_URL}/api/articles/${id}`);
                setArticle(res.data);
                setLikeCount(res.data.likeCount || 0);
                
                // Check if user has liked - handle both string and object comparison
                if (user?.id && res.data.likes) {
                    const userLiked = res.data.likes.some(likeId => {
                        const likeIdStr = typeof likeId === 'object' ? likeId.toString() : String(likeId);
                        const userIdStr = String(user.id);
                        return likeIdStr === userIdStr;
                    });
                    setIsLiked(userLiked);
                } else {
                    setIsLiked(false);
                }
            } catch (err) {
                console.error("Error fetching article details:", err);
                // 404 hatası alındıysa article'ı null olarak set et
                if (err.response?.status === 404) {
                    setArticle(null);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchArticle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, user]);

    useEffect(() => {
        if (article?.category && article?.pageType) {
            const fetchRelated = async () => {
                try {
                    const res = await axios.get(`${API_BASE_URL}/api/articles?pageType=${article.pageType}&category=${article.category}`);
                    const related = res.data
                        .filter(a => (a._id !== article._id) && (a.slug !== article.slug))
                        .slice(0, 3);
                    setRelatedArticles(related);
                } catch (err) {
                    console.error("Error fetching related articles", err);
                }
            };
            fetchRelated();
        }
    }, [article?.category, article?.pageType, article?._id, article?.slug]);

    const pageTypeSlug = (type) => {
        if (!type) return 'articles';
        switch (type.toLowerCase()) {
            case 'news': return 'news';
            case 'research': return 'research';
            case 'info': return 'info';
            case 'haber': return 'news';
            case 'araştırma': return 'research';
            case 'bilgi': return 'info';
            default: return 'articles';
        }
    };

    const handleLikeArticle = async () => {
        if (!token) {
            window.location.href = '/login';
            return;
        }

        try {
            const config = { 
                headers: { 
                    'Content-Type': 'application/json',
                    'x-auth-token': token 
                } 
            };
            const res = await axios.post(`${API_BASE_URL}/api/articles/${id}/like`, {}, config);
            setIsLiked(res.data.liked);
            setLikeCount(res.data.likeCount);
            toast.success(res.data.liked ? 'Article liked!' : 'Like removed');
        } catch (err) {
            console.error('Like article error:', err.response?.data || err.message);
            toast.error(err.response?.data?.msg || 'Failed to like article');
        }
    };

    if (loading) return <Spinner />;
    if (!article) return <div className="text-center card p-8">Article not found or not yet published.</div>;

    return (
        <>
            <SEO 
                title={article.metaTitle ? `${article.metaTitle} | AircoreDynamics` : `${article.title} | AircoreDynamics`}
                description={article.metaDescription || article.excerpt || article.content.substring(0, 160).replace(/<[^>]*>/g, '')}
                keywords={
                    (article.metaKeywords && article.metaKeywords.length > 0)
                        ? article.metaKeywords.join(', ')
                        : article.tags?.join(', ') || 'aviation, aerospace, space technology'
                }
                image={article.ogImage || article.coverImageUrl}
                url={`/articles/${article.slug || article._id}`}
                type="article"
                locale={article.pageType === 'News' ? 'tr_TR' : 'en_US'}
                article={{
                    publishedTime: article.createdAt,
                    modifiedTime: article.updatedAt,
                    author: article.author?.name,
                    section: article.pageType,
                    tags: article.tags,
                    wordCount: article.content ? article.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length : undefined,
                    readingTime: article.readTime,
                    excerpt: article.excerpt
                }}
                breadcrumb={[
                    { name: 'Home', url: '/' },
                    { name: article.pageType || 'Articles', url: `/${article.pageType?.toLowerCase() || 'articles'}` },
                    { name: article.title, url: `/articles/${article.slug || article._id}` }
                ]}
            />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto">
                <div className="card overflow-hidden">
                    <img 
                        src={article.coverImageUrl} 
                        alt={`${article.title} - ${article.category} article cover`}
                        className="w-full h-auto max-h-[500px] object-cover"
                        referrerPolicy="no-referrer"
                        onError={(e) => e.target.style.display = 'none'}
                    />
                <div className="p-8 md:p-12">
                    <div className="flex items-center text-sm font-semibold text-light-primary dark:text-dark-primary mb-2">
                        <Link to={`/${pageTypeSlug(article.pageType)}`} className="hover:underline">{article.pageType}</Link>
                        <span className="mx-2 text-light-text/50 dark:text-dark-text/50">/</span>
                        {/* Category filter link (for now just display) */}
                        <span className="text-light-text/80 dark:text-dark-text/80 font-normal">{article.category}</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold mb-4">{article.title}</h1>
                    <div className="flex items-center text-sm opacity-60 mb-8">
                        <span>Author: {article.author?.name || 'Unknown'}</span>
                        <span className="mx-2">•</span>
                        <span>Published: {new Date(article.createdAt).toLocaleDateString('en-US')}</span>
                        {article.views > 0 && (
                            <>
                                <span className="mx-2">•</span>
                                <span>{article.views.toLocaleString()} views</span>
                            </>
                        )}
                    </div>
                    <div
                        className="prose dark:prose-invert max-w-none mb-8 text-light-text dark:text-dark-text break-words overflow-wrap-anywhere"
                        dangerouslySetInnerHTML={{ __html: article.content }}
                    />

                    {/* Like & Share Buttons */}
                    <div className="flex items-center gap-4 mb-8">
                        <button
                            onClick={handleLikeArticle}
                            className="flex items-center gap-2 px-6 py-3 rounded-full bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border transition-colors group"
                        >
                            {isLiked ? (
                                <HeartIconSolid className="h-6 w-6 text-red-500" />
                            ) : (
                                <HeartIcon className="h-6 w-6 group-hover:text-red-500 transition-colors" />
                            )}
                            <span className="font-semibold">{likeCount}</span>
                        </button>
                        
                        <ShareButton 
                            url={`https://aircoredynamics.com/articles/${article.slug || article._id}`}
                            title={article.title}
                            description={article.excerpt}
                        />
                    </div>

                    {/* TAGS SECTION */}
                    {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-light-border dark:border-dark-border">
                            <span className="font-semibold mr-2 text-sm opacity-80">Tags:</span>
                            {article.tags.map(tag => (
                                <Link
                                    key={tag}
                                    to={`/${pageTypeSlug(article.pageType)}?tag=${tag}`}
                                    className="text-xs px-3 py-1 rounded-full border border-light-border dark:border-dark-border hover:bg-light-primary hover:text-white dark:hover:bg-dark-primary dark:hover:text-dark-bg transition-colors"
                                >
                                    #{tag}
                                </Link>
                            ))}
                        </div>
                    )}
                    {/* /TAGS SECTION */}

                    {/* Author Box */}
                    <AuthorBox author={article.author} />

                    {/* Related Articles */}
                    {relatedArticles.length > 0 && (
                        <div className="mt-16 pt-8 border-t border-light-border dark:border-dark-border">
                            <h3 className="text-2xl font-bold mb-6 text-light-text dark:text-dark-text">Related Articles</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {relatedArticles.map(related => (
                                    <ArticleCard key={related._id} article={related} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Comments Section */}
                    <CommentsSection articleId={article._id} />

                </div>
            </div>
        </motion.div>
        </>
    );
};

export default ArticleDetailPage;